/* eslint-disable react/jsx-key */
import { pickBy } from 'lodash';
import { FC, useMemo, useEffect, Fragment, useState, useCallback } from 'react';
import { css } from '@emotion/react';
import { Row, useTable, useSortBy, ColumnInstance, SortingRule, Column } from 'react-table';
import { Link, useHistory } from 'react-router-dom';
import { INVESTMENT_TABLE, BREAKPOINTS } from '../../constants/ui';
import BottomLineExplanationTakeover from '../BottomLineExplanationTakeover';
import Button from '../Button';
import StockLogo from '../StockLogo';
import { Rating, CollectionInvestment, BasicInvestment, Wiim } from '../../apis/backend';
import { fontStyles, spacing, colors } from '../../styles';
import BookmarkButton from '../BookmarkButton';
import chatIcon from '../Portfolios/icons/chat.svg';
import ColumnTakeover from './ColumnTakeover';
import { ColumnSelection, ColumnSelectionType, getColumns } from './columns';
import adjustIcon from './adjust.svg';
import { filterOptions, FilterKey } from './filterOptions';
import WiimPreview from './WiimPreview';
import Cell from './Cell';
import sortInactive from './sortInactive.svg';
import sortDescending from './sortDescending.svg';
import sortAscending from './sortAscending.svg';

// UI Constants
const EXPAND_BUTTON_SIZE = 28;

const sortTypes = {
  week52Range: (rowA: Row<any>, rowB: Row<any>) => {
    if (!rowA.original.week52Low || !rowA.original.week52High || !rowA.original.latestPrice) return 1;
    if (!rowB.original.week52Low || !rowB.original.week52High || !rowB.original.latestPrice) return -1;
    const calculateRatio = ({ latestPrice, week52Low, week52High }: BasicInvestment): number =>
      (latestPrice - week52Low) / (week52High - week52Low);
    const ratioA = calculateRatio(rowA.original);
    const ratioB = calculateRatio(rowB.original);
    return ratioA > ratioB ? -1 : 1;
  },

  allocation_percentage: (rowA: Row<any>, rowB: Row<any>) => {
    const allocationA = rowA.original.allocation_percentage ?? -1;
    const allocationB = rowB.original.allocation_percentage ?? -1;
    return allocationA > allocationB ? 1 : -1;
  },

  bottomLine: (rowA: Row<any>, rowB: Row<any>) => {
    const calculateBottomLinePoints = (bottomLine: { [key: string]: Rating }): number => {
      const ratingPoints: { [key in Rating]: number } = {
        [Rating.Exceptional]: 2,
        [Rating.Favorable]: 1,
        [Rating.Average]: 0,
        [Rating.Missing]: 0,
        [Rating.Unfavorable]: -1,
        [Rating.Dangerous]: -2,
      };

      const isStock = 'profitRating' in bottomLine && bottomLine.profitRating != null;

      const relevantRatings = isStock
        ? ['profitRating', 'growthRating', 'priceRating', 'insiderRating']
        : ['returnRating', 'riskRating', 'sizeRating', 'feesRating'];

      return relevantRatings.reduce(
        (previousValue, key) => previousValue + (bottomLine[key] ? ratingPoints[bottomLine[key]] : 0),
        0,
      );
    };

    const rowAPoints = calculateBottomLinePoints(rowA.original.bottomLine ?? {});
    const rowBPoints = calculateBottomLinePoints(rowB.original.bottomLine ?? {});

    return rowAPoints > rowBPoints ? -1 : 1;
  },
};
interface Props {
  isLoading?: boolean;
  investments: (Partial<BasicInvestment> | Partial<CollectionInvestment>)[];
  wiims?: { [symbol: string]: Wiim[] };
  columnSelections: ColumnSelection[];
  onChangeColumnSelections?: (index: number, value: ColumnSelection) => void;
  sortingRule: SortingRule<string>;
  onChangeSortingRule?: (rule: SortingRule<string>) => void;
  filters?: Partial<Record<FilterKey, boolean>>;
  onSetFilter?: (key: FilterKey) => void;
  showBookmark?: boolean;
  showExpandReasoning?: boolean;
  onMetricClick?: (columnId: string) => void;
  hideColumnControls?: boolean;
  availableFilters?: FilterKey[];
  realTimePrices?: Record<string, { last?: number; bid?: number; ask?: number }>;
}

const getSortIcon = (column: ColumnInstance) => {
  if (!column.isSorted) return sortInactive;
  return column.isSortedDesc ? sortDescending : sortAscending;
};

export const InvestmentTable: FC<Props> = ({
  investments,
  isLoading,
  wiims,
  columnSelections,
  onChangeColumnSelections,
  sortingRule,
  onChangeSortingRule,
  filters,
  onSetFilter,
  showBookmark = false,
  showExpandReasoning = false,
  onMetricClick,
  hideColumnControls = false,
  availableFilters,
  realTimePrices,
}) => {
  const history = useHistory();
  const [isColumnTakeoverShown, setIsColumnTakeoverShown] = useState(false);
  const [isBottomLineExplanationShown, setIsBottomLineExplanationShown] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Memoize allocation check to avoid recalculating for every expanded row
  const hasAnyAllocations = useMemo(
    () => investments.some((inv) => 'allocation_percentage' in inv && inv.allocation_percentage !== undefined),
    [investments],
  );

  // Memoize the toggle function to avoid recreating columns on every expand/collapse
  const toggleExpanded = useCallback((symbol: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      newSet.has(symbol) ? newSet.delete(symbol) : newSet.add(symbol);
      return newSet;
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        // Filter out None columns to allow users to hide columns via the column selector.
        // This enables flexible column counts (1-3 visible data columns).
        ...columnSelections
          .filter((columnSelection) => columnSelection.type !== ColumnSelectionType.None)
          .map((columnSelection) => getColumns(columnSelection)),
        {
          id: 'allocation_percentage',
          accessor: 'allocation_percentage',
          sortType: 'allocation_percentage',
          Header: '',
          Cell: () => null,
          width: 0,
          minWidth: 0,
          maxWidth: 0,
        },
        ...(showBookmark
          ? [
              {
                id: 'bookmark',
                Header: '',
                Cell: ({ row }: any) => (
                  <div
                    css={css`
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      padding: 0;
                      width: ${INVESTMENT_TABLE.BOOKMARK_COLUMN_WIDTH}px;
                      height: ${INVESTMENT_TABLE.ROW_HEIGHT}px;
                      position: relative;
                      overflow: hidden;

                      > button {
                        padding: 0;
                        transform: scale(1.5);

                        img {
                          height: 14px;
                          width: 14px;
                        }
                      }
                    `}
                  >
                    <BookmarkButton
                      symbol={row.original.symbol}
                      category={row.original.category || ''}
                      name={row.original.name || ''}
                    />
                  </div>
                ),
                width: INVESTMENT_TABLE.BOOKMARK_COLUMN_WIDTH,
              },
            ]
          : []),
        ...(showExpandReasoning
          ? [
              {
                id: 'expand',
                Header: '',
                Cell: ({ row }: { row: Row<CollectionInvestment> }) => {
                  const investment = row.original;

                  // Runtime validation for type safety
                  if (!investment || typeof investment !== 'object') return null;
                  if (!investment.symbol) return null;

                  const { symbol } = investment;
                  const isExpanded = expandedRows.has(symbol);

                  return (
                    <div
                      css={css`
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        padding: 0;
                        width: ${INVESTMENT_TABLE.BOOKMARK_COLUMN_WIDTH}px;
                        height: ${INVESTMENT_TABLE.ROW_HEIGHT}px;
                      `}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(symbol);
                        }}
                        aria-label={isExpanded ? 'Collapse reasoning' : 'Expand reasoning'}
                        aria-expanded={isExpanded}
                        css={css`
                          background: none;
                          border: none;
                          cursor: pointer;
                          padding: ${spacing.milli};
                          color: ${colors.petrolBlue};
                          font-size: 18px;
                          font-weight: bold;
                          line-height: 1;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          width: ${EXPAND_BUTTON_SIZE}px;
                          height: ${EXPAND_BUTTON_SIZE}px;

                          html[data-theme='dark'] & {
                            color: ${colors.skyBlue};
                          }

                          &:hover {
                            opacity: 0.7;
                          }
                        `}
                      >
                        {isExpanded ? '−' : '+'}
                      </button>
                    </div>
                  );
                },
                width: INVESTMENT_TABLE.BOOKMARK_COLUMN_WIDTH,
              },
            ]
          : []),
      ] as Column<Partial<BasicInvestment>>[],
    [columnSelections, showBookmark, showExpandReasoning, expandedRows, toggleExpanded],
  );
  const data = useMemo(
    () =>
      investments.filter((investment) => {
        if (!filters) {
          return investments;
        }
        return (Object.keys(pickBy(filters, (value) => value)) as FilterKey[])
          .map((key) => filterOptions[key].filterFn)
          .every((filterFn) => filterFn(investment));
      }),
    [investments, filters],
  );

  const initialState = {
    sortBy: [sortingRule],
  };

  const tableInstance = useTable({ columns, data, initialState, disableSortRemove: true, sortTypes }, useSortBy);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state, setSortBy } = tableInstance;

  useEffect(() => {
    if (onChangeSortingRule) {
      onChangeSortingRule(state.sortBy[0]);
    }
  }, [state.sortBy, onChangeSortingRule]);

  // Custom sort toggle handler to support three-state sorting
  const handleSortToggle = (column: ColumnInstance) => {
    if (column.id === 'allocation_percentage') return; // Don't allow sorting the hidden allocation column

    const currentSort = state.sortBy[0];
    const defaultSort = { id: 'allocation_percentage', desc: true };

    // If currently sorted by this column
    if (currentSort.id === column.id) {
      // If descending, go back to default (allocation)
      if (currentSort.desc) {
        setSortBy([defaultSort]);
      } else {
        // If ascending, switch to descending
        setSortBy([{ id: column.id, desc: true }]);
      }
    } else {
      // If not sorted by this column, start with ascending
      setSortBy([{ id: column.id, desc: false }]);
    }
  };

  return (
    <div>
      <BottomLineExplanationTakeover
        isShown={isBottomLineExplanationShown}
        onClose={() => setIsBottomLineExplanationShown(false)}
      />
      <ColumnTakeover
        isShown={isColumnTakeoverShown}
        columnSelections={columnSelections}
        onClose={() => setIsColumnTakeoverShown(false)}
        onChange={(index, value) => {
          if (!columnSelections || !onChangeColumnSelections) return;
          const indexOfDuplicate = columnSelections.findIndex((selection) => {
            if (value.type === ColumnSelectionType.ChangePercent)
              return value.type === selection.type && value.timeRange === selection.timeRange;
            return value.type === selection.type;
          });
          if (indexOfDuplicate >= 0) {
            onChangeColumnSelections(indexOfDuplicate, columnSelections[index]);
          }
          onChangeColumnSelections(index, value);
        }}
      />
      {filters && Object.keys(filters).length > 0 && (
        <div
          css={css`
            margin: 0 -${spacing.centi} -${spacing.milli} 0;
          `}
        >
          {(Object.keys(filters) as FilterKey[])
            .filter((key) => !availableFilters || availableFilters.includes(key))
            .map((key) => {
              const { buttonComponent } = filterOptions[key];
              const isActive = !!filters[key];

              return (
                <Button
                  key={key}
                  variant={isActive ? 'primary' : 'secondary'}
                  onClick={() => {
                    // setActiveFilters(disjointFilters ? { [key]: !isActive } : { ...activeFilters, [key]: !isActive });
                    if (onSetFilter) {
                      onSetFilter(key);
                    }
                  }}
                  css={css`
                    border-radius: 20px;
                    padding: ${spacing.milli} ${spacing.centi};
                    margin: 0 ${spacing.centi} ${spacing.centi} 0;
                    ${fontStyles.fairyfly}
                    & > * {
                      vertical-align: middle;
                    }
                  `}
                >
                  {buttonComponent}
                </Button>
              );
            })}
        </div>
      )}
      {onChangeColumnSelections && !hideColumnControls && (
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            margin: ${spacing.milli} 0 ${spacing.deci} 0;
          `}
        >
          <Button
            onClick={() => setIsColumnTakeoverShown(true)}
            css={css`
              padding: ${spacing.deci} 0;
              ${fontStyles.flea}
            `}
          >
            <img
              src={adjustIcon}
              css={css`
                vertical-align: middle;
                margin-right: ${spacing.milli};
              `}
            />
            Change columns
          </Button>

          {columnSelections.some((selection) => selection.type === 'bottomLine') && (
            <Button
              onClick={() => setIsBottomLineExplanationShown(true)}
              css={css`
                padding: ${spacing.deci} 0;
                ${fontStyles.flea}
              `}
            >
              What&apos;s the Bottom Line?
            </Button>
          )}
        </div>
      )}
      <div
        css={css`
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          /* Hide scrollbar for cleaner look */
          &::-webkit-scrollbar {
            display: none;
          }
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        `}
      >
        <table
          {...getTableProps()}
          css={css`
            width: 100%;
            min-width: ${INVESTMENT_TABLE.MIN_TABLE_WIDTH}px; /* Ensure table doesn't shrink below minimum width */
            table-layout: fixed;
            border-collapse: collapse;
            ${fontStyles.roboCat}

            @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
              font-size: 0.9rem;
            }

            /* Hide middle column on small phones */
            @media (max-width: ${BREAKPOINTS.SMALL_PHONE}px) {
              min-width: unset; /* Remove min-width constraint on small screens */

              th:nth-of-type(2),
              td:nth-of-type(2) {
                display: none;
              }

              /* Adjust column widths: Name 60%, right column 40% */
              th:first-of-type,
              td:first-of-type {
                width: 60%;
              }

              th:nth-of-type(3),
              td:nth-of-type(3) {
                width: 40%;
              }
            }
          `}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    onClick={() => column.canSort && handleSortToggle(column)}
                    css={[
                      css`
                        color: ${colors.silver};
                        padding: 0 ${spacing.milli} ${spacing.centi};
                        ${fontStyles.flea};
                        text-align: right;
                        ${column.canSort && 'cursor: pointer;'}
                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                        ${(column.id === 'bookmark' || column.id === 'expand') &&
                        `
                        width: ${INVESTMENT_TABLE.BOOKMARK_COLUMN_WIDTH}px;
                      `}
                        ${column.id === 'allocation_percentage' &&
                        `
                        display: none;
                        width: 0;
                        padding: 0;
                      `}

                        &:first-of-type {
                          text-align: left;
                        }

                        @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
                          padding: 0 ${spacing.micro} ${spacing.milli};
                          font-size: 0.7rem;
                          white-space: nowrap;
                        }
                      `,
                      column.isSorted &&
                        css`
                          color: ${colors.petrolBlue};
                          html[data-theme='dark'] & {
                            color: ${colors.skyBlue};
                          }
                        `,
                    ]}
                  >
                    <span
                      css={css`
                        vertical-align: middle;
                      `}
                    >
                      {column.render('Header')}
                    </span>
                    {column.canSort && (
                      <img
                        src={getSortIcon(column)}
                        css={css`
                          vertical-align: middle;
                          margin-left: ${spacing.centi};
                        `}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const link = `/symbol/${(row.original as CollectionInvestment).symbol}`;
              const wiim = row.original.symbol && wiims?.[row.original.symbol]?.[0];

              return (
                <Fragment key={row.getRowProps().key}>
                  <tr
                    {...row.getRowProps()}
                    css={css`
                      border-top: 1px solid ${colors.cloud};
                      height: ${INVESTMENT_TABLE.ROW_HEIGHT}px;
                      cursor: pointer;
                    `}
                    onClick={() => history.push(link)}
                  >
                    {row.cells.map((cell, index) => {
                      const columnId = cell.column.id as string;
                      const isMetricCell =
                        onMetricClick && columnId !== 'bookmark' && columnId !== 'expand' && index > 0;
                      return (
                        <td
                          {...cell.getCellProps()}
                          onClick={
                            isMetricCell
                              ? (e) => {
                                  e.stopPropagation();
                                  onMetricClick(columnId);
                                }
                              : undefined
                          }
                          css={css`
                            padding: 0 ${spacing.milli};
                            text-align: right;
                            &:first-of-type {
                              text-align: left;
                            }
                            html[data-theme='dark'] & {
                              color: ${colors.sugar};
                            }
                            @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
                              padding: 0 ${spacing.micro};
                              font-size: 0.85rem;
                            }
                            ${columnId === 'allocation_percentage' &&
                            css`
                              display: none;
                              width: 0;
                              padding: 0;
                            `}
                            ${isMetricCell &&
                            css`
                              cursor: pointer;
                            `}
                          `}
                        >
                          {cell.column.id === 'bookmark' || cell.column.id === 'expand' ? (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              css={css`
                                display: flex;
                                justify-content: center;
                                padding: 0;
                              `}
                            >
                              {cell.render('Cell')}
                            </div>
                          ) : index === 0 ? (
                            <Link
                              to={link}
                              css={css`
                                color: ${colors.liquorice};
                                display: flex;
                                align-items: center;
                                gap: ${spacing.milli};
                                html[data-theme='dark'] & {
                                  color: ${colors.sugar};
                                }

                                @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
                                  font-size: 0.85rem;
                                }
                              `}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {row.original.symbol && (
                                <StockLogo symbol={row.original.symbol} size="small" website={row.original.website} />
                              )}
                              {cell.render(Cell as any, { row, realTimePrices })}
                            </Link>
                          ) : (
                            cell.render(Cell as any, { emptyText: isLoading ? '' : '—', row, realTimePrices })
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {(() => {
                    const investment = row.original as CollectionInvestment;
                    // Calculate visible columns (exclude hidden allocation_percentage and control columns)
                    const visibleDataColumns = columns.filter(
                      (col) => col.id !== 'allocation_percentage' && col.id !== 'expand' && col.id !== 'bookmark',
                    ).length;

                    return (
                      <>
                        {wiim && (
                          <tr
                            onClick={(e) => {
                              e.stopPropagation();
                              history.push('/chat', {
                                message: `Tell me more: ${wiim.title}`,
                              });
                            }}
                            css={css`
                              cursor: pointer;
                            `}
                          >
                            <td
                              colSpan={visibleDataColumns}
                              css={css`
                                padding: 0 ${spacing.milli} ${spacing.deci} ${spacing.milli};

                                @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
                                  padding: 0 ${spacing.micro} ${spacing.centi} ${spacing.micro};
                                }
                              `}
                            >
                              <WiimPreview timestamp={wiim.created} text={wiim.title} />
                            </td>
                            {(showBookmark || showExpandReasoning) && (
                              <td
                                css={css`
                                  width: ${INVESTMENT_TABLE.BOOKMARK_COLUMN_WIDTH}px;
                                `}
                              />
                            )}
                          </tr>
                        )}
                        {showExpandReasoning && investment.symbol && expandedRows.has(investment.symbol) && (
                          <tr
                            css={css`
                              border-top: none;
                            `}
                          >
                            <td
                              colSpan={visibleDataColumns}
                              css={css`
                                padding: 0 ${spacing.milli} ${spacing.deci} ${spacing.milli};
                                vertical-align: top;

                                @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
                                  padding: 0 ${spacing.micro} ${spacing.centi} ${spacing.micro};
                                }
                              `}
                            >
                              {(() => {
                                // Determine the appropriate message
                                let message: string;
                                let showHeader = true;

                                if (investment.rationale) {
                                  // Has rationale - show it with header
                                  message = investment.rationale;
                                } else if (hasAnyAllocations) {
                                  // Allocations exist but this stock wasn't selected
                                  message = 'This stock was not selected for this strategy.';
                                  showHeader = false;
                                } else {
                                  // No allocations at all for this collection
                                  message = "This collection doesn't include explanations for each stock pick.";
                                  showHeader = false;
                                }

                                return (
                                  <div
                                    css={css`
                                      ${fontStyles.thinFlea}
                                      color: ${colors.liquorice};
                                      word-wrap: break-word;
                                      overflow-wrap: break-word;
                                      max-width: 100%;

                                      html[data-theme='dark'] & {
                                        color: ${colors.sugar};
                                      }

                                      @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
                                        font-size: 0.85rem;
                                      }
                                    `}
                                  >
                                    {showHeader && (
                                      <span
                                        css={css`
                                          font-weight: 500;
                                          margin-right: ${spacing.milli};
                                        `}
                                      >
                                        Why {investment.symbol}:
                                      </span>
                                    )}
                                    {message}
                                  </div>
                                );
                              })()}
                              <div
                                css={css`
                                  margin-top: ${spacing.deci};
                                  display: flex;
                                  gap: ${spacing.deci};
                                  align-items: center;

                                  // Override default BookmarkButton styling to make it inline
                                  button {
                                    padding: 0;
                                    min-width: auto;
                                    min-height: auto;
                                    gap: ${spacing.micro};
                                    flex-direction: row;
                                    width: fit-content;
                                    display: flex;
                                    align-items: center;
                                  }

                                  img {
                                    height: 16px;
                                    width: 16px;
                                  }

                                  span {
                                    color: ${colors.petrolBlue};
                                    ${fontStyles.flea}

                                    html[data-theme='dark'] & {
                                      color: ${colors.skyBlue};
                                    }
                                  }

                                  button:hover span {
                                    text-decoration: underline;
                                  }
                                `}
                              >
                                <BookmarkButton
                                  symbol={investment.symbol}
                                  category={investment.category || ''}
                                  name={investment.name || ''}
                                  showLabel={true}
                                  customLabel={`Bookmark ${investment.symbol}`}
                                />
                                <button
                                  css={css`
                                    background: none;
                                    border: none;
                                    cursor: pointer;
                                    padding: 0;
                                    display: flex;
                                    align-items: center;
                                    gap: ${spacing.micro};
                                  `}
                                  onClick={() => {
                                    const message = investment.rationale
                                      ? `Explain more about why ${investment.symbol}: ${investment.rationale}`
                                      : `Explain more about ${investment.symbol} and whether I should invest`;
                                    history.push('/chat', {
                                      message,
                                      from: 'collection',
                                    });
                                  }}
                                  aria-label={`Chat about ${investment.symbol}`}
                                >
                                  <img src={chatIcon} alt="Chat" />
                                  <span>Chat about {investment.symbol}</span>
                                </button>
                              </div>
                            </td>
                            {(showBookmark || showExpandReasoning) && (
                              <td
                                css={css`
                                  width: ${INVESTMENT_TABLE.BOOKMARK_COLUMN_WIDTH}px;
                                `}
                              />
                            )}
                          </tr>
                        )}
                      </>
                    );
                  })()}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export * from './columns';
export * from './filterOptions';
